import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
    Briefcase, MagnifyingGlass, MapPin, ClipboardText, Calendar, TrendUp,
} from "@phosphor-icons/react";

export default function SalesPortal() {
    const [clinics, setClinics] = useState([]);
    const [stats, setStats] = useState({});
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/sales/clinics"),
            api.get("/sales/stats"),
        ]).then(([c, s]) => {
            setClinics(c.data);
            setStats(s.data);
        }).finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        if (!q) return clinics;
        const l = q.toLowerCase();
        return clinics.filter((d) =>
            (d.name || "").toLowerCase().includes(l) ||
            (d.clinic_name || "").toLowerCase().includes(l) ||
            (d.city || "").toLowerCase().includes(l)
        );
    }, [clinics, q]);

    const cards = [
        { label: "My clinics", val: stats.clinics ?? 0, icon: Briefcase, color: "hsl(173 58% 39%)" },
        { label: "Total cases", val: stats.total_cases ?? 0, icon: ClipboardText, color: "hsl(192 100% 31%)" },
        { label: "Active cases", val: stats.active_cases ?? 0, icon: TrendUp, color: "hsl(27 87% 50%)" },
        { label: "Completed", val: stats.completed_cases ?? 0, icon: Calendar, color: "hsl(150 60% 40%)" },
    ];

    return (
        <div data-testid="sales-portal">
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold">My territory</h1>
                    <p className="text-muted-foreground mt-1 text-sm">Clinics assigned to you and their case volume.</p>
                </div>
                <div className="relative w-full max-w-xs">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search clinic, city..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 h-10"
                        data-testid="sales-search"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {cards.map((c) => (
                    <div key={c.label} className="p-4 bg-card border border-border rounded-2xl" data-testid={`sales-stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                        <c.icon size={22} weight="duotone" color={c.color} />
                        <div className="font-display text-2xl font-bold mt-2">{loading ? "…" : c.val}</div>
                        <div className="text-xs text-muted-foreground">{c.label}</div>
                    </div>
                ))}
            </div>

            {loading && <div className="text-muted-foreground">Loading…</div>}
            {!loading && filtered.length === 0 && (
                <div className="bg-card border border-border rounded-2xl p-10 text-center text-muted-foreground">
                    <Briefcase size={32} className="mx-auto mb-2 opacity-50" />
                    {clinics.length === 0 ? "No clinics assigned yet. Admin will assign your territory." : "No clinics match your search."}
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
                {filtered.map((d) => (
                    <div key={d.id} className="bg-card border border-border rounded-2xl p-5" data-testid={`sales-clinic-${d.id}`}>
                        <div className="flex items-start gap-3">
                            <img src={d.image} alt={d.name} className="w-14 h-14 rounded-xl object-cover bg-secondary" onError={(e) => { e.target.style.visibility = "hidden"; }} />
                            <div className="flex-1 min-w-0">
                                <div className="font-display text-lg font-semibold truncate">{d.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{d.specialization}</div>
                                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin size={11} /> {d.clinic_name} · {d.city}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                            <div className="bg-secondary/50 rounded-lg py-2">
                                <div className="font-bold text-lg">{d.cases_count ?? 0}</div>
                                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</div>
                            </div>
                            <div className="bg-secondary/50 rounded-lg py-2">
                                <div className="font-bold text-lg text-amber-700">{d.active_cases ?? 0}</div>
                                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Active</div>
                            </div>
                            <div className="bg-secondary/50 rounded-lg py-2">
                                <div className="font-bold text-lg">{d.appointments_count ?? 0}</div>
                                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Visits</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
